import {
  createLocalRuntimeExpectedRoutesApi,
  WakeLoopApiClientHttpError,
} from "@wakeloop/api-client";
import type { GetLocalRuntimeExpectedRoutesResponse } from "@wakeloop/schemas/types";
import { resolveBaseUrl } from "../config/base-url";
import { readDaemonServiceContract } from "../config/daemon-service-contract";
import { createCliApiClientFoundation } from "../space/api-client-foundation";

// Contract: developing/architecture/agent-transport-contract.md ("Expected Routes Projection")
// One expected-routes read per logical operation, shared by the device
// projection and the route catalog. Consumption policy:
// - resolved        -> consumers use the projection (no per-profile fan-out)
// - unavailable     -> consumers fall back to the legacy per-profile reads
//   (endpoint missing on an older server, no service contract yet, or the
//   runtime is not registered so server-side device scoping cannot apply)
// - 5xx / network   -> the read REJECTS; a failed load keeps the current
//   catalog (never shrink from an unresolved state)
// Like the binding read session, this is process-internal and
// operation-scoped by construction - never persist or reuse it.
export type ExpectedRoutesReadResult =
  | {
      response: GetLocalRuntimeExpectedRoutesResponse;
      status: "resolved";
    }
  | {
      reason:
        | "endpoint_not_available"
        | "runtime_unresolved"
        | "service_contract_missing";
      status: "unavailable";
    };

export type ExpectedRoutesReadSession = {
  read: () => Promise<ExpectedRoutesReadResult>;
};

export function createExpectedRoutesReadSession(input?: {
  baseUrl?: string;
}): ExpectedRoutesReadSession {
  let readPromise: Promise<ExpectedRoutesReadResult> | null = null;
  return {
    read: () => {
      readPromise ??= readExpectedRoutesOnce(input?.baseUrl);
      return readPromise;
    },
  };
}

async function readExpectedRoutesOnce(
  baseUrl?: string
): Promise<ExpectedRoutesReadResult> {
  const serviceContract = await readDaemonServiceContract().catch(() => null);
  const deviceId = serviceContract?.deviceId?.trim() ?? "";
  const lane = serviceContract?.lane?.trim() ?? "";
  if (!(deviceId && lane)) {
    return {
      reason: "service_contract_missing",
      status: "unavailable",
    };
  }

  const api = createLocalRuntimeExpectedRoutesApi(
    createCliApiClientFoundation(baseUrl ?? (await resolveBaseUrl()))
  );
  try {
    const response = await api.getExpectedRoutes({ deviceId, lane });
    if (response.runtimeResolution !== "resolved") {
      return {
        reason: "runtime_unresolved",
        status: "unavailable",
      };
    }
    return {
      response,
      status: "resolved",
    };
  } catch (error) {
    if (error instanceof WakeLoopApiClientHttpError && error.status === 404) {
      return {
        reason: "endpoint_not_available",
        status: "unavailable",
      };
    }
    throw error;
  }
}
